import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import TextareaField from '../ui/TextareaField';
import { CancelSaveMode } from '../../api/booking/bookingWriteApiSlice';

/** The server refuses a longer reason rather than cutting it (`DECLINE_REASON_MAX`). */
export const DECLINE_REASON_MAX = 1000;

interface Props {
    open: boolean;
    loading: boolean;
    onClose: () => void;
    onConfirm: (saveMode: CancelSaveMode, reason: string | null) => void;
}

/**
 * "Decline" for a room reservation still awaiting approval (M8, #1809).
 *
 * A decline is a cancel: the booking ends "Storniert · abgelehnt", the
 * customer gets the decline mail instead of the cancellation mail. Like the
 * cancel dialog, both commits are buttons, never a checkbox: whether the
 * customer is told is a choice the operator makes on purpose.
 *
 * The reason is optional and internal: it is shown on the booking detail to
 * staff only, never in the mail (PO, 2026-10-04).
 *
 * Shared by the venue and apartment detail pages, which are near-copies; an
 * inline dialog in each would drift on the next edit.
 */
const DeclineBookingDialog = ({ open, loading, onClose, onConfirm }: Props) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [pending, setPending] = useState<CancelSaveMode | null>(null);

    // A fresh dialog each time: a reason typed for one booking must not sit
    // in the box when the next one is declined.
    useEffect(() => {
        if (open) {
            setReason('');
            setPending(null);
        }
    }, [open]);

    if (!open) return null;

    const tooLong = reason.trim().length > DECLINE_REASON_MAX;
    const commit = (saveMode: CancelSaveMode) => {
        setPending(saveMode);
        onConfirm(saveMode, reason.trim() || null);
    };

    return (
        <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
            <div className={'fixed inset-0 bg-black bg-opacity-50 transition-opacity'} onClick={onClose} />
            <div
                className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-auto'}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={'decline-dialog-title'}
            >
                <div className={'p-6 flex flex-col gap-4'}>
                    <h3 className={'text-lg font-bold text-foreground'} id={'decline-dialog-title'}>
                        {t('booking_details.decline_dialog.header')}
                    </h3>
                    <p className={'text-sm text-foreground'}>
                        {t('booking_details.decline_dialog.content')}
                    </p>
                    <TextareaField
                        labelAbove
                        label={t('booking_details.decline_dialog.reason_label')}
                        placeholder={t('booking_details.decline_dialog.reason_placeholder')}
                        value={reason}
                        onChange={setReason}
                        rows={3}
                        disabled={loading}
                    />
                    {tooLong && (
                        <p className={'text-sm text-foreground'} role={'alert'}>
                            {t('booking_details.decline_dialog.reason_too_long', { max: DECLINE_REASON_MAX })}
                        </p>
                    )}
                </div>
                <div className={'px-6 py-4 flex flex-row-reverse flex-wrap gap-3'}>
                    <CustomButton
                        title={t('booking_details.decline_dialog.decline_and_send')}
                        loading={loading && pending === 'save_and_send_email'}
                        disabled={loading || tooLong}
                        onClick={() => commit('save_and_send_email')}
                    />
                    <CustomButton
                        title={t('booking_details.decline_dialog.decline_only')}
                        loading={loading && pending === 'save_only'}
                        disabled={loading || tooLong}
                        onClick={() => commit('save_only')}
                        isFilled={false}
                    />
                    <div className={'mr-auto'}>
                        <CustomButton
                            title={t('booking_details.decline_dialog.abort')}
                            onClick={onClose}
                            isFilled={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeclineBookingDialog;
